import type { OperationsDayResponse } from '@/shared/api';
import { RULES } from './heuteTodoRules';

export type HeuteTodoAction =
  | { kind: 'link'; label: string; to: string; search?: Record<string, string> }
  | { kind: 'apartment'; label: string; propertyId: string }
  | { kind: 'none' };

/** Welche Zielseiten der Benutzer öffnen darf (wie die Routen-Guards). */
export interface TodoAccess {
  staff: boolean;
  cleaning: boolean;
  bookings: boolean;
}

export interface HeuteTodo {
  code: string;
  tag: string;
  title: string;
  detail: string;
  count: number;
  /** Rot nur für „muss heute jemand tun“ (Baukasten). */
  hot: boolean;
  action: HeuteTodoAction;
}

const ORDER = Object.keys(RULES);
/** Kein Auftrag, sondern ein Zustand: steht unten bei den ruhigen Apartments. */
const NOT_A_TODO = new Set(['manual_block']);
/** Alle Codes, die diese Seite kennt; ein Test hält sie gegen die Liste des Servers. */
export const KNOWN_ATTENTION_CODES = [...ORDER, ...NOT_A_TODO];

function namesOf(data: OperationsDayResponse, ids: string[]): string {
  // Ein Apartment außerhalb der Tagesliste (gelöscht, verschoben) heißt so – kein erfundener Name.
  const names = ids.map(
    (id) =>
      data.apartments.find((a) => a.propertyId === id)?.adminDisplayName ?? 'unbekanntes Apartment',
  );
  return names.length > 3
    ? `${names.slice(0, 3).join(', ')} +${names.length - 3}`
    : names.join(', ');
}

function allowed(action: HeuteTodoAction, access: TodoAccess): HeuteTodoAction {
  if (action.kind !== 'link') return action;
  const ok = action.to.startsWith('/cleaning')
    ? access.cleaning
    : action.to === '/bookings'
      ? access.bookings
      : access.staff;
  // Ohne Recht sprang der Link still auf „Heute“ zurück; die Karte bleibt, nur ohne Ziel.
  return ok ? action : { kind: 'none' };
}

/** Nummerierte Aufgaben für Heute: je Hinweis-Code eine Karte, betroffene Apartments darin. */
export function heuteTodos(data: OperationsDayResponse, access: TodoAccess): HeuteTodo[] {
  const byCode = new Map<string, { count: number; ids: string[] }>();
  for (const item of data.attention) {
    if (NOT_A_TODO.has(item.code) || item.count <= 0) continue;
    const entry = byCode.get(item.code) ?? { count: 0, ids: [] };
    entry.count += item.count;
    if (item.propertyId && !entry.ids.includes(item.propertyId)) entry.ids.push(item.propertyId);
    byCode.set(item.code, entry);
  }
  const rank = (code: string) => (ORDER.includes(code) ? ORDER.indexOf(code) : ORDER.length);
  return [...byCode.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([code, { count, ids }]) => {
      const known = RULES[code];
      const [one, many] = known?.unit ?? ['Fall', 'Fälle'];
      const detail = ids.length ? namesOf(data, ids) : `${count} ${count === 1 ? one : many}`;
      if (!known) {
        // Ein Code, den diese Oberfläche noch nicht kennt: sichtbar mit Code, ohne erfundenes Ziel.
        return {
          code,
          tag: 'Hinweis',
          title: 'Weiterer Hinweis',
          detail: `${code} · ${detail}`,
          count,
          hot: false,
          action: { kind: 'none' },
        };
      }
      return {
        code,
        tag: known.tag,
        title: known.title,
        detail,
        count,
        hot: known.hot,
        action: allowed(known.action(data, ids), access),
      };
    });
}
